import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ApplicantService } from '../../service/applicant.service';
import { ToastrService } from 'ngx-toastr';
import { debounceTime, Subject } from 'rxjs';
import { Router } from '@angular/router';
import { SharedApplicantService } from '../../shared/services/shared-applicant.service';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { EgTableComponent } from '../../shared/components/eg-table/eg-table.component';
import { MatTabChangeEvent, MatTabsModule } from '@angular/material/tabs';
import { MatCardModule } from '@angular/material/card';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule, MatHint, MatLabel } from '@angular/material/form-field';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE, MatOptionModule } from '@angular/material/core';
import { MatInputModule } from '@angular/material/input';
import { MatDialog } from '@angular/material/dialog';
import { ApplicantListDialogComponent } from './applicant-list-dialog/applicant-list-dialog.component';
import { DateTime } from 'luxon';
import { LuxonDateAdapter, MAT_LUXON_DATE_ADAPTER_OPTIONS, MAT_LUXON_DATE_FORMATS } from '@angular/material-luxon-adapter';
import { getApplicantFormControls } from './applicant-clients/form-controls.section';
import { adaSections } from './applicant-clients/ada-form-sections';
import { medicaidFormSections } from './applicant-clients/medicaid-form-sections';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { UserManagementService } from '../../service/user-management.service';

const ROLES = ['SUPER_ADMIN','ADMIN','CASE_MANAGER'];

export const MEDICAID_COLUMNS = [
  'firstName', 'lastName', 'sisId', 'email', 'mobileNo', 'actionsApplicantList'
];
export const ADA_COLUMNS = [
  'firstName', 'lastName', 'email', 'mobileNo', 'actionsApplicantList'
];

const MEDICAID_DISPLAYED_COLUMNS = [
  { key: 'firstName', label: 'First Name' },
  { key: 'lastName', label: 'Last Name' },
  { key: 'email', label: 'Email' },
  { key: 'sisId', label: 'SIS ID' },
  { key: 'mobileNo', label: 'Mobile' },
  { key: 'actionsApplicantList', label: 'Actions' }
];

const ADA_DISPLAYED_COLUMNS = [
  { key: 'firstName', label: 'First Name' },
  { key: 'lastName', label: 'Last Name' },
  { key: 'email', label: 'Email' },
  { key: 'mobileNo', label: 'Primary Phone Number' },
  { key: 'actionsApplicantList', label: 'Actions' }
];

@Component({
  selector: 'app-applicant-list',
  standalone: true,
  imports: [CommonModule, FormsModule, MatPaginatorModule, EgTableComponent, MatTabsModule, ReactiveFormsModule, MatCardModule, MatDatepickerModule, MatHint, MatLabel, MatFormFieldModule, MatInputModule 
    ,MatTabsModule,MatCardModule,MatFormFieldModule,MatInputModule,MatSelectModule,MatOptionModule,MatDatepickerModule,MatButtonModule, MatIconModule,
    MatButtonModule,MatProgressSpinnerModule],
  providers: [
    { provide: DateAdapter, useClass: LuxonDateAdapter, deps: [MAT_DATE_LOCALE, MAT_LUXON_DATE_ADAPTER_OPTIONS] },
    { provide: MAT_DATE_FORMATS, useValue: MAT_LUXON_DATE_FORMATS },
    { provide: MAT_LUXON_DATE_ADAPTER_OPTIONS, useValue: { useUtc: true } }
  ],
  templateUrl: './applicant-list.component.html',
  styleUrls: ['./applicant-list.component.scss'],
})
export class ApplicantListComponent implements OnInit {
  applicants: any[] = [];
  applicantsData: any;
  isLoading: boolean = false;
  searchTerm: string = '';
  pageNo = 0;
  limit = 10;
  start = 0;  // Start index of the current page
  end = 10;   // End index of the current page
  totalElements = 0;  // Total number of elements in all pages
  pageSize = 10;
  pageIndex = 0;
  
  cols: any = [];
  displayedColumns: any = [];
  private readonly searchSubject: Subject<string> = new Subject();
  public addApplicantForm: FormGroup = this.formBuilder.group({});
  searchPID : any = '';
  showPassword: { [key: string]: boolean } = {};
  ids = ['extra2','extra3','SSN4'];
  maxDate = DateTime.now().toISODate();
  minDate = DateTime.fromObject({ year: 1900, month: 1, day: 1 }).toISODate(); // '1900-01-01'
  statesList: { key: string; value: string; }[] = [];
  sections: { id: number; name: string; fields: { id: string; label: string; type: string; control_name: string; required: boolean; maxlength?: number; options?: { key: string; value: string; }[]; pattern?: string; }[]; }[] = [];
  selectedTabIndex = 0;
  isEligibilityAutoDetermined: boolean = false;
  tabNames = ['Applicant List', 'Add Applicant'];
  isAddEditApplicantAllowed: boolean = false;
  isAddMode: boolean = true;

  constructor(
    private readonly applicantService: ApplicantService,
    private readonly sharedApplicantService: SharedApplicantService,
    private readonly toastr: ToastrService,
    private readonly router: Router,
    private readonly formBuilder: FormBuilder,
    private readonly dialog: MatDialog,
    private readonly userManagementService: UserManagementService
  ) {}

  openDialog() {
    const dialogRef = this.dialog.open(ApplicantListDialogComponent, {
      width: '48rem',
      minWidth: '48rem',
      height: '29rem',
      data: {}
    });
 
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.isLoading = true;
        // Handle the result here
        this.uploadApplicantData(result);
      }
    });
  }

  uploadApplicantData(file: File) {
    const formData = new FormData();
    formData.append('file', file);
 
    this.applicantService.uploadApplicantData(formData).subscribe({
      next: (response: Blob) => {
        this.isLoading = false;
        const fileURL = window.URL.createObjectURL(response);
        const link = document.createElement('a');
        link.href = fileURL;
        link.download = 'Applicants Data Upload Report.xlsx';

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        setTimeout(() => window.URL.revokeObjectURL(fileURL), 100);
        this.toastr.success(`Applicants Data Upload Report download started ...`, '', {
          timeOut: 1000,
        });
        this.loadApplicants(this.pageNo, this.pageSize, this.searchTerm);
      },
      error: (error) => {
        this.isLoading = false;
        console.error('Error uploading applicant data:', error);
        this.toastr.error('Error uploading applicant data');
        this.userManagementService.redirectToLogin(error,'');
      }
    });
  }

  ngOnInit(): void {
    this.isEligibilityAutoDetermined = sessionStorage.getItem('isEligibilityAutoDetermined') === 'true';
    const userRole = sessionStorage.getItem('userRole');
    
    if (this.isEligibilityAutoDetermined) {
      // Only SUPER_ADMIN can Add/Edit Applicant, others have no access
      this.isAddEditApplicantAllowed = ROLES.includes(userRole || '') && userRole === 'SUPER_ADMIN';
      this.cols = MEDICAID_COLUMNS;
      this.displayedColumns = MEDICAID_DISPLAYED_COLUMNS;
      this.sections = medicaidFormSections;
    } else {
      // SUPER_ADMIN, ADMIN, and CASE_MANAGER can Add/Edit Applicant
      this.isAddEditApplicantAllowed = ROLES.includes(userRole || '');
      this.cols = ADA_COLUMNS;
      this.displayedColumns = ADA_DISPLAYED_COLUMNS;
      this.sections = adaSections;
    }
    this.loadApplicants(0, 10, '');
    console.log(sessionStorage.getItem('isAdmin'));
    // Subscribe to the search subject for debouncing
    this.searchSubject.pipe(debounceTime(300)).subscribe((keyword) => {
      if (keyword.length >= 3) {
        this.loadApplicants(this.pageNo, this.pageSize, keyword);
      } else if (keyword.length === 0) {
        this.loadApplicants(this.pageNo, this.pageSize); // Load all applicants if search is cleared
      }
    });
    this.addApplicantForm = this.formBuilder.group(getApplicantFormControls(this.sections));
  }
  profileActions(event: any) {
    if(event.applicantAction == 'view') {
      this.tabNames[1] = 'Add Applicant';
      this.sharedApplicantService.changeApplicantId(event.applicantId);
      this.router.navigate(['/applicant-detail']);
    }
    else if(event.applicantAction == 'edit') {
      this.tabNames[1] = 'Edit Applicant';
      this.addApplicantForm.controls['extra2']?.disable();
      this.isAddMode = false;
      this.addApplicantForm.markAsUntouched();
      this.sharedApplicantService.changeApplicantId(event.applicantId);
      this.applicantService.fetchApplicantDetails(event.applicantId).subscribe({
        next: (response: any) => {
          if(response) {
            this.addApplicantForm.patchValue(response?.applicantDetailsAsJson);
            if((response?.applicantDetailsAsJson?.phone_mobile) && !(response?.applicantDetailsAsJson?.phone_mobile.includes('-'))) {
              this.addApplicantForm.controls['phone_mobile'].patchValue(this.formatFetchedPhoneNumber(response.applicantDetailsAsJson?.phone_mobile));
            }
            if((response?.applicantDetailsAsJson?.phone_landline) && !(response?.applicantDetailsAsJson?.phone_landline.includes('-'))) {
              this.addApplicantForm.controls['phone_landline'].patchValue(this.formatFetchedPhoneNumber(response.applicantDetailsAsJson?.phone_landline));
            }
            if((response?.applicantDetailsAsJson?.emergency_phone_day) && !(response?.applicantDetailsAsJson?.emergency_phone_day.includes('-'))) {
              this.addApplicantForm.controls['emergency_phone_day'].patchValue(this.formatFetchedPhoneNumber(response.applicantDetailsAsJson?.emergency_phone_day));
            }
            this.selectedTabIndex = 1;
          }
        },
        error: (error: any) => {
          console.error('There was an error fetching applicant details:', error);
        }
      });
    }
  }

  loadApplicants(pageNo?:any, limit?:any ,keyword: string = '') {
    this.isLoading = true;
    this.applicantService.getApplicants(pageNo.toString(), limit.toString(),keyword).subscribe({
      next: (response: any) => {
        this.applicantsData = response;
        if (response?.content) {
          this.start = 0;
          this.end = this.applicantsData?.numberOfElements;
          this.totalElements = this.applicantsData?.totalElements;
          this.applicants = response.content.map((applicant: any) => {
            const firstName = applicant.applicantDetailsAsJson?.first_name || '';
            const lastName = applicant.applicantDetailsAsJson?.last_name || '';
            const sisId = applicant.applicantDetailsAsJson?.extra3 || '';
            const email = applicant.applicantDetailsAsJson?.email || '';
            const mobileNo = this.isEligibilityAutoDetermined ?
            applicant.applicantDetailsAsJson?.extra7  : applicant.applicantDetailsAsJson?.phone_landline || '';

            return {
              ...applicant,
              firstName: firstName,
              lastName: lastName,
              sisId: sisId,
              email: email,
              mobileNo: mobileNo,
              profilePicUrl: applicant.profilePicUrl || '',
              status: 'Under Review',
              submissionDate: new Date(
                applicant.createdDate
              ).toLocaleDateString(),
            };
          });
        } else {
          console.error(
            'Error: No content or applicants found in the response.'
          );
          this.applicants = [];
        }

        this.isLoading = false;
      },
      error: (error: any) => {
        console.error('There was an error loading applicants:', error);
        this.isLoading = false;
        this.toastr.error('There was an error loading applicants. Please try again.');
        this.userManagementService.redirectToLogin(error,'');
      }
    });
  }

  onSearch(event: Event) {
    const input = event.target as HTMLInputElement;
    const keyword = input.value;
    this.searchSubject.next(keyword);
  }

  clearSearch() {
    this.searchTerm = '';
    this.loadApplicants(0, this.pageSize, '');
  }
  clearPIDSearch() {
    this.searchPID = '';
  }
  public getPaginatorData(event: PageEvent): PageEvent {
    this.pageSize = event.pageSize;
    this.pageIndex = event.pageIndex;
    this.loadApplicants(this.pageIndex.toString(), this.pageSize.toString(),this.searchTerm);
    return event;
  }
  addApplicant() {
    this.patchDropdownValues();
    this.sections.forEach((section: any) => {
      section.fields.forEach((field: any) => {
        if(field.type == 'select') {
          if(this.addApplicantForm.controls[field.control_name]?.value == '') {
            if(field.required) {
              this.addApplicantForm.controls[field.control_name].setErrors({required: true});
              this.addApplicantForm.controls[field.control_name].markAsTouched();
            }
          }
        }
      });
    });
    
    if(this.addApplicantForm.invalid) {
      this.toastr.error('Please fill all the required fields.');
      this.addApplicantForm.markAllAsTouched();
    }
    else {
      this.isLoading = true;
      // Fix: Use fromISO and set zone to 'utc' to avoid off-by-one error
      const dobValue = this.addApplicantForm.controls['date_of_birth']?.value;
      if (dobValue) {
        // If value is already a Date object, convert to ISO string first
        let isoString = typeof dobValue === 'string' ? dobValue : new Date(dobValue).toISOString();
        // Use Luxon fromISO and set zone to UTC
        const dob = DateTime.fromISO(isoString, { zone: 'utc' });
        this.addApplicantForm.controls['date_of_birth']?.patchValue(dob.toFormat('yyyy-MM-dd'));
      }
      let payload = JSON.parse(JSON.stringify(this.addApplicantForm.value));
      if(this.addApplicantForm?.controls['home_county']) {
        payload.home_county = {
          id: this.addApplicantForm?.controls['home_county']?.value
        };
      }
      if(this.addApplicantForm?.controls['extra7']?.value?.includes('-')) {
        payload.extra7 = this.addApplicantForm?.controls['extra7']?.value.replaceAll('-', '');
      }
      if(this.addApplicantForm?.controls['phone_mobile']?.value?.includes('-')) {
        payload.phone_mobile = this.addApplicantForm?.controls['phone_mobile']?.value.replaceAll('-', '');
      }
      if(this.addApplicantForm?.controls['emergency_phone_day']?.value?.includes('-')) {
        payload.emergency_phone_day = this.addApplicantForm?.controls['emergency_phone_day']?.value.replaceAll('-', '');
      }
      if(this.addApplicantForm?.controls['phone_landline']?.value?.includes('-')) {
        payload.phone_landline = this.addApplicantForm?.controls['phone_landline']?.value.replaceAll('-', '');
      }
      payload.is_add_record_from_ui = this.isAddMode;
      payload.extra2 = this.addApplicantForm?.controls['extra2']?.value ? this.addApplicantForm?.controls['extra2']?.value : null;
      console.log(this.addApplicantForm.controls['date_of_birth'].value);
      
      this.applicantService.addNewApplicant(payload).subscribe({
        next: (data: any) => {
          this.isLoading = false;
          this.resetApplicantData();
          this.addApplicantForm.setErrors(null);
          Object.keys(this.addApplicantForm.controls).forEach(key => {
            this.addApplicantForm.get(key)?.setErrors(null);
            this.addApplicantForm.get(key)?.markAsPristine();
            this.addApplicantForm.get(key)?.markAsUntouched();
          });
          if(this.isAddMode) {
            this.toastr.success('Applicant added successfully.');
          }
          else {
            this.toastr.success('Applicant updated successfully.');
          }
        },
        error: (error) => {
          this.isLoading = false;
          this.toastr.error(error?.error || 'There was an error adding new applicant. Please try again.');
          console.error('There was an error adding applicant:', error);
          if(error.status === 401) {
            this.userManagementService.redirectToLogin(error,'');
          }
        }
      });
    }

  }
  tabChanged = (tabChangeEvent: MatTabChangeEvent): void => {
    if(tabChangeEvent.index == 0) {
      this.searchTerm = '';
      this.tabNames[1] = 'Add Applicant';
      this.isAddMode = true;
      this.addApplicantForm.controls['extra2']?.enable();
      this.addApplicantForm.reset();
    }
    this.loadApplicants(0, 10, '');
  }
  onNumberInput(event: any, control_name: any) {
   if(control_name == 'phone_landline' || control_name == 'extra7' || control_name == 'phone_mobile' || control_name == 'emergency_phone_day' || control_name == 'contact_primary_phone_number'
    || control_name == 'contact_alternate_phone_number' || control_name == 'emergency_primary_phone_number' || control_name == 'emergency_alternate_phone_number'
   ) {
        const input = event.target as HTMLInputElement;
        input.value= this.formatPhoneNumber(event.target.value);
    }
  }
  formatPhoneNumber(value: string): string {
    value = value?.replace(/\D/g, ''); // Remove all non-numeric characters
    if (value?.length >= 4 && value?.length < 7) {
      return value.replace(/(\d{3})(\d+)/, '$1-$2');
    }
    else if (value?.length >= 7 && value?.length < 10) {
      return value.replace(/(\d{3})(\d{3})(\d+)/, '$1-$2-$3');
    }
    else if (value?.length === 10) {
      return value.replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3');
    }
    return value;
  }
  formatFetchedPhoneNumber(phoneNo: any) {
    return [phoneNo?.slice(0, 3), "-", phoneNo?.slice(3,6), "-", phoneNo?.slice(6)].join('');
  }
  getApplicantData() {
    this.isLoading = true;
    this.applicantService.fetchApplicantByPid(this.searchPID).subscribe({
      next: (data: any) => {
        if(data) {
          this.toastr.success('Applicant found with the given PID.');
          this.addApplicantForm.patchValue(data);
          this.sections.forEach((section: any) => {
            section.fields.forEach((field: any) => {
              if(field.type == 'select') {
                if(field.options && !(field.options.some((option: any) => option.key == data[field.control_name]))) {
                  this.addApplicantForm.controls[field.control_name].patchValue('');
                }
              }
            });
          });
          if((data?.extra7) && !(data?.extra7.includes('-'))) {
            this.addApplicantForm.controls['extra7'].patchValue(this.formatFetchedPhoneNumber(data.extra7));
          }
          if((data?.phone_mobile) && !(data?.phone_mobile.includes('-'))) {
            this.addApplicantForm.controls['phone_mobile'].patchValue(this.formatFetchedPhoneNumber(data.phone_mobile));
          }
          if((data?.emergency_phone_day) && !(data?.emergency_phone_day.includes('-'))) {
            this.addApplicantForm.controls['emergency_phone_day'].patchValue(this.formatFetchedPhoneNumber(data.emergency_phone_day));
          }
          this.addApplicantForm.controls['home_county'].patchValue(data?.home_county?.id);
        }
        else {
          this.resetApplicantData();
          this.addApplicantForm.markAsUntouched();
          this.toastr.error('No applicant found with the given PID.');
        }
        this.isLoading = false;
      },
      error: (error) => {
        this.isLoading = false;
        this.toastr.error('There was an error fetching applicant data. Please try again.');
        this.userManagementService.redirectToLogin(error,'');
        console.error('There was an error fetching applicant data:', error);
      }
    });
  }
  togglePasswordVisibility(param: any) {
    this.ids.forEach((id: any) => {
      if(id == param) {
        this.showPassword[id] = !this.showPassword[id];
        if(this.showPassword[id]){
          document.getElementById(param)?.setAttribute('type', 'text');
        } else {
          document.getElementById(param)?.setAttribute('type', 'password');
        }
      }
    }); 
  }
  resetApplicantData() {
    this.addApplicantForm.reset();
    this.patchDropdownValues();
  }
  patchDropdownValues() {
    this.sections.forEach((section: any) => {
      section.fields.forEach((field: any) => {
        if(field.type == 'select') {
          this.addApplicantForm.controls[field.control_name]?.patchValue(
            this.addApplicantForm.controls[field.control_name]?.value || ''
          );
        }
      });
    });
  }

  dateOfBirthMinValidator(control: any) {
    if (!control.value) return null;

    const inputDate = DateTime.fromISO(control.value);
    const minDate = DateTime.fromJSDate(this.minDate);

    if (inputDate < minDate) {
      return { minDate: true };
    }
    return null;
  }
  
  allowOnlyNumbers(event: KeyboardEvent): void {
  const charCode = event.key.charCodeAt(0);
    // Allow only digits (0-9)
    if (charCode < 48 || charCode > 57) {
      event.preventDefault();
    }
  }
}